import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fileUrl, fmtDate } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/constants";

const AMBER = [180, 83, 9];
const DARK = [30, 27, 24];

async function logoDataUrl(path) {
  if (!path) return null;
  try {
    const res = await fetch(fileUrl(path), { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const img = await new Promise((ok, bad) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = bad;
      i.src = url;
    });
    const scale = Math.min(1, 300 / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * scale);
    c.height = Math.round(img.naturalHeight * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    return { data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight };
  } catch (e) {
    return null;
  }
}

const num = (n) => Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export async function downloadSalesReport({ report, settings, periodLabel, filename }) {
  const sym = settings.currency_symbol || "$";
  const m = (n) => `${sym}${num(n)}`;
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pw = doc.internal.pageSize.getWidth();
  const logo = await logoDataUrl(settings.logo_path);

  let x = 40;
  if (logo) {
    const h = 54;
    const w = Math.min((logo.w / logo.h) * h, 110);
    doc.addImage(logo.data, "PNG", 40, 36, w, h, undefined, "FAST");
    x = 50 + w;
  }
  doc.setFont("helvetica", "bold").setFontSize(15).setTextColor(...DARK).text(settings.name || "", x, 50);
  doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(90);
  const info = [settings.rfc && `RFC: ${settings.rfc}`, settings.address, [settings.phone, settings.email].filter(Boolean).join(" · ")].filter(Boolean);
  info.forEach((l, i) => doc.text(l, x, 63 + i * 10));
  doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(...AMBER).text("HISTORIAL DE VENTAS", pw - 40, 50, { align: "right" });
  doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(60).text(periodLabel, pw - 40, 64, { align: "right" });
  doc.text(`Generado: ${new Date().toLocaleString("es-MX")}`, pw - 40, 76, { align: "right" });
  doc.setDrawColor(...AMBER).setLineWidth(2).line(40, 100, pw - 40, 100);

  const s = report.summary;
  autoTable(doc, {
    startY: 112,
    theme: "grid",
    head: [["Notas", "Canceladas", "Subtotal", "Descuentos", settings.tax_name || "Impuesto", "Total vendido", "Cobrado (pagadas)", "Saldo por cobrar"]],
    body: [[s.count, s.cancelled, m(s.subtotal), m(s.discount), m(s.tax), m(s.total), m(s.paid), m(s.pending)]],
    styles: { fontSize: 8, halign: "center" },
    headStyles: { fillColor: AMBER, textColor: 255 },
    bodyStyles: { fontStyle: "bold" },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 18,
    head: [["Folio", "Fecha", "Cliente", "Estado", "Subtotal", "Desc.", settings.tax_name || "Imp.", "Total", "Anticipo", "Saldo"]],
    body: report.sales.map((v) => [v.folio, fmtDate(v.date), v.client_name, STATUS_LABEL[v.status] || v.status,
      m(v.subtotal), m(v.discount), m(v.tax), m(v.total), m(v.advance), m(v.balance)]),
    foot: [["", "", "TOTAL (sin canceladas)", "", m(s.subtotal), m(s.discount), m(s.tax), m(s.total), m(s.advance), m(s.pending)]],
    styles: { fontSize: 7.5, cellPadding: 3 },
    headStyles: { fillColor: DARK, textColor: 240 },
    footStyles: { fillColor: [253, 230, 138], textColor: DARK, fontStyle: "bold" },
    columnStyles: { 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" } },
  });

  if (report.sales.length) {
    let y = doc.lastAutoTable.finalY + 26;
    if (y > 700) { doc.addPage(); y = 50; }
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(...DARK).text("Detalle por nota de venta", 40, y);
    y += 8;
    report.sales.forEach((v) => {
      const client = [v.client_name, v.client_phone && `Tel. ${v.client_phone}`, v.client_email, v.client_rfc && `RFC ${v.client_rfc}`, v.client_address].filter(Boolean).join(" · ");
      autoTable(doc, {
        startY: y + 8,
        head: [[{ content: `${v.folio}  ·  ${fmtDate(v.date)}  ·  ${STATUS_LABEL[v.status] || v.status}${v.quote_folio ? `  ·  De cotización ${v.quote_folio}` : ""}`, colSpan: 4 }],
          [{ content: client, colSpan: 4, styles: { fontStyle: "normal", fillColor: [245, 245, 244], textColor: 60 } }],
          ["Concepto", "Cant.", "P. unitario", "Importe"]],
        body: [
          ...v.items.map((i) => [i.description, i.quantity, m(i.unit_price), m(i.quantity * i.unit_price)]),
          ...(v.notes ? [[{ content: `Notas: ${v.notes}`, colSpan: 4, styles: { textColor: 90, fontStyle: "italic" } }]] : []),
        ],
        foot: [[{ content: `Subtotal ${m(v.subtotal)}   Desc. ${m(v.discount)}   ${settings.tax_name || "Imp."} (${v.tax_rate}%) ${m(v.tax)}   TOTAL ${m(v.total)}   Anticipo ${m(v.advance)}   Saldo ${m(v.balance)}`, colSpan: 4, styles: { halign: "right" } }]],
        styles: { fontSize: 7.5, cellPadding: 3 },
        headStyles: { fillColor: AMBER, textColor: 255 },
        footStyles: { fillColor: [250, 250, 249], textColor: DARK, fontStyle: "bold" },
        columnStyles: { 1: { halign: "right", cellWidth: 45 }, 2: { halign: "right", cellWidth: 80 }, 3: { halign: "right", cellWidth: 80 } },
        rowPageBreak: "avoid",
      });
      y = doc.lastAutoTable.finalY + 6;
    });
  }

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(7).setTextColor(140).text(`${settings.name} · Historial de ventas · Página ${p} de ${pages}`, pw / 2, doc.internal.pageSize.getHeight() - 20, { align: "center" });
  }
  doc.save(filename);
}
